// 3rd
import _ from 'underscore';

import replicationSpecListUtils from './replicationSpecListUtils';

import { CLUSTER_TYPE } from 'js/project/nds/clusterEditor/NDSClusterForm/schema';
import { VALID_NDS_INSTANCE_NAME_REGEX } from 'js/project/nds/common/utils/constants';
import { CloudProvider, InstanceSizes, Instance, InstanceSize } from 'js/project/nds/types/provider';
import { ClusterDescription } from 'js/project/nds/types/clusterDescription';
import { ReplicationSpecList } from 'js/project/nds/types/replicationSpec';
import { RegionName } from 'js/project/nds/types/region';
import { findLoadSampleDatasetStatusForCluster } from 'js/project/nds/clusters/util/loadSampleDatasetUtils';

// types
import { LoadSampleDatasetStatus } from 'js/project/nds/types/loadSampleDataset';

interface CanLoadSampleDatasetArgs {
  clusterDetail: any;
  loadSampleDatasetStatus?: LoadSampleDatasetStatus;
  loadSampleDatasetStatusCollection?: Array<LoadSampleDatasetStatus>;
}

const MIN_FREE_PERCENTAGE_DISKSPACE_WITH_OPLOG = 0.25;
// See AutomationDiffValidationSvc.java#L1050 for 3.6+ 990MB oplogSizeMB requirement.
const MIN_OPLOG_SIZE_MB_360_PLUS = 990;
const MAX_DEFAULT_OPLOG_SIZE_MB = 50 * 1024;

const DEFAULT_OPLOG_SIZE_PERCENT = 0.05;
const NVME_OPLOG_SIZE_PERCENT = 0.1;
const CLUSTER_NAME_UNIQUE_PREFIX_LENGTH = 23;

function _getProviderCounts(clusterDescriptions, providers) {
  return clusterDescriptions.reduce((accum, curr) => {
    const preferredProvider = replicationSpecListUtils.getMostCommonPreferredBackingCloudProvider(
      curr.replicationSpecList
    );
    accum[preferredProvider]++;
    return accum;
  }, providers);
}

export const MAX_PAUSED_DAYS = 30;

export const MINIMUM_IO1_IOPS = 100;

export function isNameValid(name) {
  return name !== '' && VALID_NDS_INSTANCE_NAME_REGEX.test(name);
}

export function isNameUnique(name, clusters) {
  return !clusters.find((clusterName) => clusterName === name);
}

export function isNamePrefixUnique(name, clusters) {
  return !clusters.find(
    (clusterName) =>
      clusterName.substring(0, CLUSTER_NAME_UNIQUE_PREFIX_LENGTH) ===
      name.substring(0, CLUSTER_NAME_UNIQUE_PREFIX_LENGTH)
  );
}

export function isNameEndWithHyphen(name) {
  return name.endsWith('-') || name.charAt(CLUSTER_NAME_UNIQUE_PREFIX_LENGTH - 1) === '-';
}

export function getDefaultProvider(clusterDescriptions: Array<ClusterDescription>, isPrivateIpModeEnabled = false) {
  // the following works for 'regular' as well as tenant clusters:
  // check the preferred region of a cluster's replication spec;
  // default to the most often-preferred provider
  if (clusterDescriptions.length === 0) return isPrivateIpModeEnabled ? 'GCP' : 'AWS';

  let providerCounts;
  if (isPrivateIpModeEnabled) {
    providerCounts = _getProviderCounts(clusterDescriptions, { GCP: 0, AZURE: 0 });
  } else {
    providerCounts = _getProviderCounts(clusterDescriptions, { AWS: 0, GCP: 0, AZURE: 0 });
  }

  return _.max(Object.keys(providerCounts), (p) => providerCounts[p]);
}

export function hasFreeCluster(clusterDescriptions: Array<ClusterDescription>): boolean {
  return clusterDescriptions.some(
    (cd) => replicationSpecListUtils.getFirstInstanceSize(cd.replicationSpecList) === InstanceSizes.M0
  );
}

export function hasPaidCluster(clusterDescriptions: Array<ClusterDescription>): boolean {
  return clusterDescriptions.some(
    (cd) => replicationSpecListUtils.getFirstInstanceSize(cd.replicationSpecList) !== InstanceSizes.M0
  );
}

export function isShardedCluster(clusterType): boolean {
  return clusterType === CLUSTER_TYPE.SHARDED || clusterType === CLUSTER_TYPE.GEOSHARDED;
}

export function isGeoShardedCluster(clusterType): boolean {
  return clusterType === CLUSTER_TYPE.GEOSHARDED;
}

export function isFreeCluster(instanceSize: string): boolean {
  return instanceSize === 'M0';
}

export function isTenantCluster(instanceSize: string | undefined): boolean {
  return instanceSize ? ['M0', 'M2', 'M5'].indexOf(instanceSize) !== -1 : false;
}

export function isServerlessCluster(instanceSize: string): boolean {
  return instanceSize.indexOf('SERVERLESS') != -1;
}

export function isDedicatedCluster(instanceSize: string): boolean {
  return !isTenantCluster(instanceSize) && !isServerlessCluster(instanceSize);
}

export function isPausedDedicated(clusterDescription): boolean {
  return (
    clusterDescription.isPaused() &&
    !replicationSpecListUtils
      .getCloudProviders(clusterDescription.getReplicationSpecList())
      .includes(CloudProvider.FREE)
  );
}

export function getInstanceSizeDisplay(size: string): string {
  return (
    size &&
    size
      .replace(/_NVME$/, '')
      .replace(/^R/, 'M')
      .replace(/^M0$/, 'M0 Sandbox')
  );
}

export function getInstanceSizeDisplayWithClass(size, isLowCPU, isNVMe) {
  const instanceSize = getInstanceSizeDisplay(size);
  let instanceClass;
  if (isLowCPU) {
    instanceClass = 'Low CPU';
  } else if (isNVMe) {
    instanceClass = 'NVMe SSD';
  } else {
    instanceClass = 'General';
  }
  return `${instanceSize} (${instanceClass})`;
}

export function formatDiskSizeString(diskSizeGB) {
  if (diskSizeGB < 1) {
    return `${diskSizeGB * 1024} MB`;
  }
  return `${diskSizeGB} GB`;
}

export function shouldDisableActions(clusterDescription) {
  return (
    clusterDescription.isDeleting() ||
    clusterDescription.isCreating() ||
    clusterDescription.isPaused() ||
    clusterDescription.isTenantUpgrading() ||
    clusterDescription.isDecomposedShardedCluster()
  );
}

export function isDiskBackupAllowed(providers) {
  return providers.some((provider) => provider === 'AZURE' || provider === 'AWS' || provider === 'GCP');
}

export function getMinProvisionedIOPS(instanceSize, diskSizeGB) {
  const minIOPSForInstance = instanceSize.minIOPS;
  const minIOPSPerGB = instanceSize.minIOPSPerGB;
  const minIOPS = Math.max(minIOPSForInstance, minIOPSPerGB * diskSizeGB);

  const maxIOPSForInstance = instanceSize.maxIOPS || instanceSize.maxStandardIOPS;
  return Math.min(minIOPS, maxIOPSForInstance);
}

export function getMaxReadIOPS(instance, diskSizeGB) {
  const { maxReadIOPS, readIopsPerGB } = instance;
  return Math.min(readIopsPerGB * diskSizeGB, maxReadIOPS);
}

export function getMaxWriteIOPS(instance, diskSizeGB) {
  const { maxWriteIOPS, writeIopsPerGB } = instance;
  return Math.min(writeIopsPerGB * diskSizeGB, maxWriteIOPS);
}

export function getStandardIOPS(instanceSize, diskSizeGB) {
  const minProvisionedIOPS = getMinProvisionedIOPS(instanceSize, diskSizeGB);
  const maxStandardIOPSForInstance = instanceSize.maxStandardIOPS;

  return Math.min(minProvisionedIOPS, maxStandardIOPSForInstance);
}

export function getStandardIOPSForGP3(instanceSize, diskSizeGB) {
  const minIOPSForInstance = 3000;
  const minIOPSPerGB = 3;
  const minIOPS = Math.max(minIOPSForInstance, minIOPSPerGB * diskSizeGB);

  const maxIOPSForInstance = instanceSize.maxIOPS || instanceSize.maxStandardIOPS;
  const maxStandardIOPSForInstance = instanceSize.maxStandardIOPS;

  return Math.min(minIOPS, maxIOPSForInstance, maxStandardIOPSForInstance);
}

export function getProvisionedIOPS(instanceSize, diskSizeGB) {
  const minStandardIOPS = getStandardIOPS(instanceSize, diskSizeGB);
  const minProvisionedIOPS = getMinProvisionedIOPS(instanceSize, diskSizeGB);

  const minIOPS = Math.max(minStandardIOPS, minProvisionedIOPS);
  const maxIOPS = getMaxProvisionedIOPS(instanceSize, diskSizeGB);

  return Math.floor((maxIOPS - minIOPS) / 2 + minIOPS);
}

export function getMaxProvisionedIOPS(instanceSize, diskSizeGB) {
  const maxIOPSForInstance = instanceSize.maxIOPS;
  const maxIOPSPerGB = instanceSize.maxIOPSPerGB;

  return Math.min(maxIOPSForInstance, maxIOPSPerGB * diskSizeGB);
}

export function getSteppedDiskSizeGB(instanceSize, diskSizeGB, useInstanceSizeMaxStorage = false) {
  // Map the specified diskSizeGB to the storage option closest to the user's selected value
  const options = useInstanceSizeMaxStorage
    ? instanceSize.storageOptionsGB
    : instanceSize.storageOptionsGB.filter((size) => size <= instanceSize.maxAllowedStorageGB);
  const differences = options.map((size) => Math.abs(size - diskSizeGB));
  const minDifference = differences.reduce((a, b) => Math.min(a, b), Number.MAX_VALUE);
  return options[differences.indexOf(minDifference)];
}

export function calculateThroughput(providers, providerOptions, diskSizeGB, instance, useInstanceSizeMaxStorage) {
  if (providers.length === 1 && providers[0] === 'AZURE') {
    const mappedStorage = getSteppedDiskSizeGB(instance, diskSizeGB, useInstanceSizeMaxStorage);
    const options = providerOptions.AZURE.diskSizes[mappedStorage];
    const maxThroughput = instance.maxThroughput;
    if (options) {
      return (maxThroughput && Math.min(options.throughputMBPerSecond, maxThroughput)) || 0;
    }
  }
  return 0;
}

export function ensureDiskSizeInBound(minStorageGB, maxStorageGB, diskSizeGB) {
  let ensuredDiskSizeGB = diskSizeGB;
  if (diskSizeGB <= minStorageGB) {
    ensuredDiskSizeGB = minStorageGB;
  } else if (diskSizeGB >= maxStorageGB) {
    ensuredDiskSizeGB = maxStorageGB;
  }
  return ensuredDiskSizeGB;
}

export function calculateMaxAllowedOplogSizeMB(
  diskSizeGB,
  clusterMaxDiskUsageMB,
  currentOplogUsageSizeMB,
  currentlySetOplogSizeMB: number | undefined = undefined,
  isNVMe = false
) {
  const maxAllowedOplogSizeMB =
    diskSizeGB * 1024 * (1 - MIN_FREE_PERCENTAGE_DISKSPACE_WITH_OPLOG) -
    (clusterMaxDiskUsageMB - currentOplogUsageSizeMB);

  // The maxAllowedOplogSize should not be any lower than the currently set oplog size
  return Math.max(
    Math.floor(maxAllowedOplogSizeMB),
    currentlySetOplogSizeMB || Math.floor(getDefaultOplogSizeMB(diskSizeGB, isNVMe))
  );
}

// Default oplog size is 5% (default - MongoDB) or 10% (NVMe - Atlas imposed) of free disk space with a lower bound of 990MB and upper bound of 50GB
// See https://docs.mongodb.com/manual/core/replica-set-oplog/ for details on the MongoDB default
export function getDefaultOplogSizeMB(diskSizeGB, isNVMe) {
  const defaultSize = Math.floor(diskSizeGB * 1024 * (isNVMe ? NVME_OPLOG_SIZE_PERCENT : DEFAULT_OPLOG_SIZE_PERCENT));
  return Math.min(Math.max(MIN_OPLOG_SIZE_MB_360_PLUS, defaultSize), MAX_DEFAULT_OPLOG_SIZE_MB);
}

export function isOplogSizeMBValid(
  processArgs,
  originalProcessArgs,
  diskSizeGB,
  clusterMaxDiskUsageMB,
  currentOplogUsageSizeMB,
  mongoDBVersion,
  isNVMe
) {
  const maxAllowedOplogSizeMB = calculateMaxAllowedOplogSizeMB(
    diskSizeGB,
    clusterMaxDiskUsageMB,
    currentOplogUsageSizeMB,
    originalProcessArgs.oplogSizeMB,
    isNVMe
  );
  const errorMsg = getOplogSizeMBErrorMsg(
    processArgs,
    originalProcessArgs,
    diskSizeGB,
    mongoDBVersion,
    maxAllowedOplogSizeMB,
    isNVMe
  );
  return errorMsg === '';
}

export function getOplogSizeMBErrorMsg(
  processArgs,
  originalProcessArgs,
  diskSizeGB?,
  mongoDBVersion?,
  maxAllowedOplogSizeMB?,
  isNVMe?
) {
  const oplogSizeMB = processArgs.oplogSizeMB;

  if (oplogSizeMB == null && originalProcessArgs.oplogSizeMB == null) {
    return '';
  }

  if (oplogSizeMB == null && originalProcessArgs.oplogSizeMB != null) {
    return 'Your oplog size cannot be empty';
  }

  if (oplogSizeMB <= 0) {
    return 'Your oplog size cannot be less than or equal to 0';
  }

  const reg = /^\d+$/;
  if (!reg.test(oplogSizeMB)) {
    return 'Your oplog size is not an integer';
  }

  if (parseFloat(mongoDBVersion) >= 3.6 && oplogSizeMB < MIN_OPLOG_SIZE_MB_360_PLUS) {
    return `Your oplog size cannot be changed to < ${MIN_OPLOG_SIZE_MB_360_PLUS} MB on MongoDB 3.6+`;
  }

  const minOplogSizeMB = Math.min(diskSizeGB * 1024 * NVME_OPLOG_SIZE_PERCENT, MAX_DEFAULT_OPLOG_SIZE_MB);
  if (isNVMe && oplogSizeMB < minOplogSizeMB) {
    return `Your oplog size must be at least ${minOplogSizeMB} MB for instances using local NVMe SSDs.`;
  }

  if (oplogSizeMB > maxAllowedOplogSizeMB) {
    return `
        Your oplog size cannot exceed ${maxAllowedOplogSizeMB.toLocaleString()} MB available. Atlas requires at least
        25% of free disk space for usage excluding the oplog. Consider upgrading your disk size if you need a larger
        oplog.`;
  }

  return '';
}

export function updateAutoIndexing(clusterDescription, newAutoIndexing) {
  const { replicationSpecList } = clusterDescription;
  const newReplicationSpecList = replicationSpecListUtils.updateAutoIndexing(newAutoIndexing, replicationSpecList);
  return {
    ...clusterDescription,
    replicationSpecList: newReplicationSpecList,
  };
}

export function isNVMe(providerOptions, provider, instanceSize) {
  if (provider === 'SERVERLESS') {
    return false;
  }
  return !!providerOptions[provider].instanceSizes[instanceSize].isNVMe;
}

export function getTotalAnalyticsNodes(replicationSpecList: ReplicationSpecList): number {
  return replicationSpecList.reduce((accum, spec) => accum + replicationSpecListUtils.getTotalAnalyticsNodes(spec), 0);
}

export function allowContinuousBackupForVersion(mongoDBVersion: string): boolean {
  return parseFloat(mongoDBVersion) < 4.2;
}

export function usesIndexKeyLimit(mongoDBVersion: string): boolean {
  return parseFloat(mongoDBVersion) <= 4.0;
}

export function getDisabledComputeAutoScaling(compute = {}) {
  return {
    ...compute,
    enabled: false,
    scaleDownEnabled: false,
    minInstanceSize: null,
    maxInstanceSize: null,
  };
}

export function getRamFromRamPerInstanceFamily(instance: Instance, regionName: RegionName): number {
  const providers = new Set(instance.availableRegions.map((region) => region.providerName));
  // For cross-cloud, get the smallest ram size
  if (providers.size > 1) {
    return Object.values(instance.ramPerInstanceFamily).sort().shift() || 0.0;
  }

  const availableFamiliesInRegion =
    instance.availableRegions.find((region) => region.regionName === regionName)?.availableFamilies || [];

  const instanceFamilyToUse = [...availableFamiliesInRegion]
    .reverse() // prefer the latest InstanceFamily for the ram selection
    .find((instanceFamily) => instance.ramPerInstanceFamily[instanceFamily] !== undefined);

  return instanceFamilyToUse ? instance.ramPerInstanceFamily[instanceFamilyToUse] : 0.0;
}

export function canLoadSampleDataset({
  clusterDetail,
  loadSampleDatasetStatus,
  loadSampleDatasetStatusCollection,
}: CanLoadSampleDatasetArgs) {
  const clusterDescription = clusterDetail.getClusterDescription();
  const deploymentItem = clusterDetail.getDeploymentItem();
  const liveImport = clusterDetail.getLiveImport();
  const actionsDisabled = shouldDisableActions(clusterDescription);
  let loadSampleDataStatus = loadSampleDatasetStatus;
  if (!loadSampleDataStatus) {
    loadSampleDataStatus = loadSampleDatasetStatusCollection
      ? findLoadSampleDatasetStatusForCluster(loadSampleDatasetStatusCollection, clusterDescription)?.attributes
      : null;
  }

  return (
    // Not Serverless:
    !clusterDescription.isServerless() &&
    // Is a tenant cluster or has one deployed item
    (isTenantCluster(clusterDescription.getInstanceSize()) || !!deploymentItem) &&
    // Cluster is ready to receive data:
    !actionsDisabled &&
    !liveImport?.isRunning() &&
    // Sample data addition hasn't been initiated already:
    loadSampleDataStatus?.state !== 'WORKING'
  );
}

export function instanceSizeToComputeUnit(instanceSize: InstanceSize) {
  if (isServerlessCluster(instanceSize)) {
    return 'Serverless Instance';
  }
  if (isTenantCluster(instanceSize)) {
    return 'Shared Cluster';
  }
  return 'Dedicated Cluster';
}
